<template><div><h2 id="layout-stops" tabindex="-1" class="d-docsite--header-2">Layout stops <a class="header-anchor d-link d-docsite--link d-link" href="#layout-stops" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-w-{stop}</code> to set a fixed width for an element using layout token stops. The hyphen before the number indicates a layout token reference, e.g. <code v-pre>d-w-100</code> outputs <code v-pre>inline-size: var(--dt-layout-100)</code> (64px).</p>
<dt-notice kind="info" title="Scale-indexed vs pixel-indexed stops" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Bare integer stops (<code v-pre>25</code>, <code v-pre>50</code>, <code v-pre>100</code>, …) are scale-indexed on the 64px base — <code v-pre>value_in_px = stop × 64 / 100</code>, so <code v-pre>25</code> = 16px and <code v-pre>100</code> = 64px. Stops with a <code v-pre>px</code> suffix (<code v-pre>1px</code>, <code v-pre>2px</code>, <code v-pre>8px</code>, <code v-pre>20px</code>, <code v-pre>24px</code>) are off-scale exceptions that encode the literal pixel value.
</dt-notice>
<code-example source-code='&lt;div class=&quot;d-w-100&quot;&gt;...&lt;/div&gt;  &lt;!-- inline-size: var(--dt-layout-100) = 64px / 6.4rem --&gt;
&lt;div class=&quot;d-w-200&quot;&gt;...&lt;/div&gt;  &lt;!-- inline-size: var(--dt-layout-200) = 128px / 12.8rem --&gt;
&lt;div class=&quot;d-w-400&quot;&gt;...&lt;/div&gt;  &lt;!-- inline-size: var(--dt-layout-400) = 256px / 25.6rem --&gt;'>
<div v-dt-scrollbar:never class="d-bar-400 d-d-flex d-bgc-secondary d-w100p d-hmx-500 d-ta-center">
  <dt-stack gap="100" align="start">
    <div v-for="(i, index) in layout" v-dt-tooltip="{ message: `${i.px}px`, delay: false }">
      <dt-text kind="code" size="100" class="d-w-100 d-us-all">d-w-{{i.stop}}</dt-text>
      <div class="d-h-100 d-bgc-moderate d-bar-300" :class="`d-w-${i.stop}`"></div>
    </div>
  </dt-stack>
</div>
</code-example>
<h2 id="percentages" tabindex="-1" class="d-docsite--header-2">Percentages <a class="header-anchor d-link d-docsite--link d-link" href="#percentages" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-w{n}p</code> to set a percentage width for an element. No hyphen before the number, <code v-pre>p</code> suffix indicates a literal percentage value. Note: <code v-pre>d-w33p</code> = 33.333% and <code v-pre>d-w66p</code> = 66.667%.</p>
<code-example source-code='&lt;div class=&quot;d-w25p&quot;&gt;25%&lt;/div&gt;
&lt;div class=&quot;d-w50p&quot;&gt;50%&lt;/div&gt;
&lt;div class=&quot;d-w75p&quot;&gt;75%&lt;/div&gt;
&lt;div class=&quot;d-w100p&quot;&gt;100%&lt;/div&gt;'>
<div v-dt-scrollbar:never class="d-bar-400 d-bgc-secondary d-w100p d-hmx-500">
  <div>
    <dt-stack as="div" gap="200" align="center" justify="center" class="d-w100p">
      <div v-for="i in percentage" v-dt-tooltip="{ message: `${i}%`, delay: false }" class="d-bgc-moderate d-w100p">
        <div class="d-bgc-moderate-opaque d-bar-300 d-p-100" :class="`d-w${i}p`">
          <dt-text kind="code" size="100" class="d-us-all">d-w{{i}}p</dt-text>
        </div>
      </div>
    </dt-stack>
  </div>
</div>
</code-example>
<h2 id="viewport" tabindex="-1" class="d-docsite--header-2">Viewport <a class="header-anchor d-link d-docsite--link d-link" href="#viewport" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use viewport width utilities to size an element relative to the viewport. <code v-pre>d-w100vw</code> (no hyphen, literal CSS value) sets <code v-pre>inline-size: 100vw</code>. The dynamic viewport variants use a hyphen: <code v-pre>d-w-dvw</code>, <code v-pre>d-w-svw</code>, <code v-pre>d-w-lvw</code>.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>dvw</strong> (dynamic) -- adapts to mobile browser chrome appearing/disappearing.</li>
<li class="d-docsite--list-element"><strong>svw</strong> (small) -- minimum viewport size, when mobile browser chrome is visible.</li>
<li class="d-docsite--list-element"><strong>lvw</strong> (large) -- maximum viewport size, when mobile browser chrome is hidden.</li>
</ul>
<code-example only-show="code" source-code='&lt;div class=&quot;d-w100vw&quot;&gt;...&lt;/div&gt;   &lt;!-- inline-size: 100vw --&gt;
&lt;div class=&quot;d-w-dvw&quot;&gt;...&lt;/div&gt;    &lt;!-- inline-size: 100dvw --&gt;
&lt;div class=&quot;d-w-svw&quot;&gt;...&lt;/div&gt;    &lt;!-- inline-size: 100svw --&gt;
&lt;div class=&quot;d-w-lvw&quot;&gt;...&lt;/div&gt;    &lt;!-- inline-size: 100lvw --&gt;'>
<div class="d-w100vw">...</div>   <!-- inline-size: 100vw -->
<div class="d-w-dvw">...</div>    <!-- inline-size: 100dvw -->
<div class="d-w-svw">...</div>    <!-- inline-size: 100svw -->
<div class="d-w-lvw">...</div>    <!-- inline-size: 100lvw -->
</code-example>
<h2 id="character-width" tabindex="-1" class="d-docsite--header-2">Character width <a class="header-anchor d-link d-docsite--link d-link" href="#character-width" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-w{n}ch</code> to constrain width based on character count. Useful for limiting text line lengths for readability.</p>
<code-example only-show="code" source-code='&lt;div class=&quot;d-w60ch&quot;&gt;...&lt;/div&gt;   &lt;!-- inline-size: 60ch --&gt;
&lt;div class=&quot;d-w75ch&quot;&gt;...&lt;/div&gt;   &lt;!-- inline-size: 75ch --&gt;
&lt;div class=&quot;d-w90ch&quot;&gt;...&lt;/div&gt;   &lt;!-- inline-size: 90ch --&gt;'>
<div class="d-w60ch">...</div>   <!-- inline-size: 60ch -->
<div class="d-w75ch">...</div>   <!-- inline-size: 75ch -->
<div class="d-w90ch">...</div>   <!-- inline-size: 90ch -->
</code-example>
<h2 id="keywords" tabindex="-1" class="d-docsite--header-2">Keywords <a class="header-anchor d-link d-docsite--link d-link" href="#keywords" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use keyword utilities to set width using CSS keyword values.</p>
<code-example only-show="code" source-code='&lt;div class=&quot;d-w-auto&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-w-unset&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-w-fit-content&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-w-max-content&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-w-min-content&quot;&gt;...&lt;/div&gt;'>
<div class="d-w-auto">...</div>
<div class="d-w-unset">...</div>
<div class="d-w-fit-content">...</div>
<div class="d-w-max-content">...</div>
<div class="d-w-min-content">...</div>
</code-example>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<clamped-table-wrapper>
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-ibs-0">
        <tr>
          <th scope="col" class="d-p-0 d-bbw0 d-w30p"><div class="d-p-200 d-bb d-bbw1">Class</div></th>
          <th scope="col" class="d-p-0 d-bbw0"><div class="d-p-200 d-bb d-bbw1">Output</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="i in layout">
          <th scope="row" class="d-code--sm d-docsite-code">.d-w-{{ i.stop }}</th>
          <td class="d-code--sm">inline-size: var(--dt-layout-{{ i.stop }}) !important; <span class="d-fc-tertiary">/* {{ i.px }}px / {{ (i.px / 10).toFixed(1) }}rem */</span></td>
        </tr>
      </tbody>
      <tbody>
        <tr v-for="i in percentage">
          <th scope="row" class="d-code--sm d-docsite-code">.d-w{{ i }}p</th>
          <td class="d-code--sm">inline-size: {{ i === 33 ? '33.333' : i === 66 ? '66.667' : i }}% !important;</td>
        </tr>
      </tbody>
      <tbody>
        <tr v-for="i in viewport.width">
          <th scope="row" class="d-code--sm d-docsite-code">.d-w{{ i.includes('vw') ? i : `-${i}` }}</th>
          <td class="d-code--sm">inline-size: {{ i.includes('vw') ? i : `100${i}` }} !important;</td>
        </tr>
      </tbody>
      <tbody>
        <tr v-for="i in characterWidth">
          <th scope="row" class="d-code--sm d-docsite-code">.d-w{{ i }}</th>
          <td class="d-code--sm">inline-size: {{ i }} !important;</td>
        </tr>
      </tbody>
      <tbody>
        <tr v-for="i in keywords">
          <th scope="row" class="d-code--sm d-docsite-code">.d-w-{{ i }}</th>
          <td class="d-code--sm">inline-size: {{ i }} !important;</td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
</div></template>


<script setup>
  import { percentage, layout, viewport, characterWidth, keywords } from '@data/width-height.json';
  import ClampedTableWrapper from '@baseComponents/ClampedTableWrapper.vue';
</script>